// identity: backoffice users, their memberships at a location or account, the roles that grant them catalog
// permissions, and invitations (parity/api-platform/employees-roles-permissions.md §3.8, §3.11; backoffice
// settings-order-staff-timecards.md §6 "Users" and §7 "User Roles"). Sign-in, sessions, passkeys and device tokens are
// the server's session transport (`/auth/v1/*`, server plan §4.4), not operations: they set cookies and read
// proof-of-possession headers the operation envelope does not carry.
//
// One model for every backoffice principal (platform target model §4.2): a user holds memberships; each membership
// names a role; a role is a set of catalog permission keys. "Super Admin" is the one system role: it grants every key
// a merchant can hold, cannot be edited or deleted, and is what legacy's "no role" meant.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { permissions, type PermissionKey } from '../../permissions/catalog.ts';
import { AccountId, InviteId, IsoInstant, LocationId, RoleId, UserId } from '../../primitives/codecs.ts';
import { LocationZone } from '../tenancy/index.ts';

/** The system role's fixed id (every location has it; it is never stored as a row). */
export const SUPER_ADMIN_ROLE_ID = '0199a000-0000-7000-8000-000000000001';

// A person's keys only: a device permission (kitchen.operate, KDS-P-01) is held by a unit's kind, never by a role or user.
const PERMISSION_KEYS = (Object.keys(permissions) as PermissionKey[]).filter((k) => permissions[k].audience !== 'device') as [PermissionKey, ...PermissionKey[]];
export const PermissionKeyEnum = z.enum(PERMISSION_KEYS).meta({ id: 'PermissionKey' });

/**
 * Keys only Radish staff and resellers hold (legacy "AR" fields); a merchant role can never grant them. Every
 * Radish-staff-only key below is one too (B830: Commander's equipment.manage and lab.devices.manage were missing, so a
 * merchant's Super Admin held them and a custom role could grant them).
 */
export const PLATFORM_ONLY_PERMISSIONS: readonly PermissionKey[] = [
  'settings.platform.manage', 'billing.catalog.manage', 'billing.subscription.manage', 'billing.adjustment.grant', 'users.platform.view', 'users.platform.manage', 'tenancy.lifecycle.manage', 'staff.desk.read', 'staff.support.read', 'staff.onboarding.read', 'staff.locations.read', 'support.db.read',
  'onboarding.menu.manage', 'fleet.read', 'fleet.command.basic', 'fleet.command.alert', 'fleet.command.router', 'fleet.screen.view', 'fleet.screen.control', 'support.case.manage', 'support.case.read', 'lab.manage', 'equipment.manage', 'lab.devices.manage', 'support.bugs.read', 'support.bugs.manage', 'qr.premade.manage',
];

/**
 * Keys only Radish staff hold, never a reseller: the billing catalog and plan prices (billing plan §4.4), the staff
 * Users page's platform-wide user list and its Create User and Edit User (legacy REQUIRES_ADMIN), and a location's
 * lifecycle (a training location sends no customer mail; lane C round 9).
 */
export const RADISH_STAFF_ONLY_PERMISSIONS: readonly PermissionKey[] = ['billing.catalog.manage', 'users.platform.view', 'users.platform.manage', 'tenancy.lifecycle.manage', 'staff.desk.read', 'staff.support.read', 'staff.onboarding.read', 'staff.locations.read', 'support.db.read', 'onboarding.menu.manage', 'fleet.read', 'fleet.command.basic', 'fleet.command.alert', 'fleet.command.router', 'fleet.screen.view', 'fleet.screen.control', 'support.case.manage', 'support.case.read', 'lab.manage', 'equipment.manage', 'lab.devices.manage', 'support.bugs.read', 'support.bugs.manage'];

/** What Super Admin grants: every catalog key except the platform-only ones. */
export const SUPER_ADMIN_PERMISSIONS: readonly PermissionKey[] = PERMISSION_KEYS.filter((k) => !PLATFORM_ONLY_PERMISSIONS.includes(k));

/**
 * Keys only a Super Admin (and Radish staff or a reseller signed in as the user) holds; a custom role can never grant
 * them. Legacy's back office opened Users and User Roles only for a Super Admin (route meta `requiresSuperAdmin`,
 * the sidebar's `isSuperAdmin`), whatever a custom role's Users toggle said. Andrew, 2026-10-13 (A53 (a)): match
 * legacy, and a legacy role with Users on gains nothing at migration (plan 2026-10-13-users-pages-super-admin). The
 * card Radish bills is the Super Admin's too (billing plan §4.4, slice 5).
 */
export const SUPER_ADMIN_ONLY_PERMISSIONS: readonly PermissionKey[] = ['users.manage', 'billing.payment_method.manage'];

/** What a custom role may grant: Super Admin's keys less the Super Admin-only ones. */
export const CUSTOM_ROLE_PERMISSIONS: readonly PermissionKey[] = SUPER_ADMIN_PERMISSIONS.filter((k) => !SUPER_ADMIN_ONLY_PERMISSIONS.includes(k));

/**
 * The keys a back-office role's grants hold: a back-office child key (Sales Reports under View Reports, Online Store
 * Orders under Manage Online Store) only with its parent. Legacy disabled a child's toggle while its parent was off but
 * still opened the child's page, whose every call then failed on the parent's API check (B-row, platform PR).
 * Register (staff) keys are left as they are: their parents are toggles of another kind.
 */
export function heldPermissions(keys: readonly PermissionKey[]): PermissionKey[] {
  const held = new Set(keys);
  return keys.filter((k) => {
    const def: { readonly audience: string; readonly parent?: string } = permissions[k];
    return def.audience !== 'user' || def.parent === undefined || held.has(def.parent as PermissionKey);
  });
}

/** An email address as typed; the server compares and stores it lowercased. */
const Email = z.email().max(254);

export const RoleView = z
  .object({
    roleId: RoleId,
    name: z.string(),
    description: z.string().nullable(),
    isSystem: z.boolean(),
    permissions: z.array(PermissionKeyEnum),
    /** Members at the location holding the role. */
    memberCount: z.int().nonnegative(),
  })
  .meta({ id: 'RoleView' });

export const MemberView = z
  .object({
    userId: UserId,
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    roleId: RoleId,
    roleName: z.string(),
    /** 'account': the member opens every location of the account; managed on the account, not here. */
    scope: z.enum(['location', 'account']),
    lastLoginAt: IsoInstant.nullable(),
  })
  .meta({ id: 'MemberView' });

export const InviteView = z
  .object({
    inviteId: InviteId,
    email: z.string(),
    invitedBy: z.string().nullable(),
    roleId: RoleId,
    roleName: z.string(),
    sentAt: IsoInstant,
    expiresAt: IsoInstant,
    expired: z.boolean(),
  })
  .meta({ id: 'InviteView' });

/**
 * How an invitation's link reaches the invitee: `email` (the default) mails it; `link` mails nothing and hands the link
 * to the inviting admin once, to pass on themselves ("Copy invite link"). Either way only the link's hash is stored, it
 * expires in seven days and works once.
 */
export const InviteDelivery = z.enum(['email', 'link']).meta({ id: 'InviteDelivery' });

/** An invitation as its sender sees it right after sending; `acceptUrl` only for delivery `link`, and only this once. */
export const SentInvite = InviteView.extend({
  /** The one-time accept link (D7 secret: the stored result a retry replays leaves it out, and logs never carry it). */
  acceptUrl: secret(z.url()).optional(),
}).meta({ id: 'SentInvite' });

export const RoleNotFound = defineError({
  code: 'identity.role_not_found', httpStatus: 404, message: 'errors.identity.role_not_found', retryable: false,
  details: z.object({ roleId: RoleId }),
});
/** Legacy 403 "System roles cannot be modified." / "System roles cannot be deleted.". */
export const SystemRoleLocked = defineError({
  code: 'identity.system_role_locked', httpStatus: 403, message: 'errors.identity.system_role_locked', retryable: false,
  details: z.object({ roleId: RoleId }),
});
export const MemberNotFound = defineError({
  code: 'identity.member_not_found', httpStatus: 404, message: 'errors.identity.member_not_found', retryable: false,
  details: z.object({ userId: UserId }),
});
/** Legacy 422 "You cannot change your own role." / "You cannot remove yourself from this location.". */
export const OwnMembership = defineError({
  code: 'identity.own_membership', httpStatus: 422, message: 'errors.identity.own_membership', retryable: false,
  details: z.object({}),
});
/** Legacy 422 "Cannot remove the only admin from this location.". */
export const LastAdmin = defineError({
  code: 'identity.last_admin', httpStatus: 422, message: 'errors.identity.last_admin', retryable: false,
  details: z.object({}),
});
/** An account-wide member is managed on the account, not from one location's page. */
export const AccountMember = defineError({
  code: 'identity.account_member', httpStatus: 422, message: 'errors.identity.account_member', retryable: false,
  details: z.object({ userId: UserId }),
});
/** Legacy 422 "This user is already assigned to this location.". */
export const AlreadyMember = defineError({
  code: 'identity.already_member', httpStatus: 422, message: 'errors.identity.already_member', retryable: false,
  details: z.object({}),
});
/** Legacy "The user is already invited to this location" (legacy answered 500; decided 409 here). */
export const AlreadyInvited = defineError({
  code: 'identity.already_invited', httpStatus: 409, message: 'errors.identity.already_invited', retryable: false,
  details: z.object({ inviteId: InviteId }),
});
/**
 * Too many invitation mails to one address, or sent by one person, in an hour (B1059: nothing capped them, so a loop of
 * Resend could mail-bomb an address). Retry after `retryAfterSeconds`.
 */
export const InviteMailLimited = defineError({
  code: 'identity.invite_mail_limited', httpStatus: 429, message: 'errors.identity.invite_mail_limited', retryable: true,
  details: z.object({ retryAfterSeconds: z.int().positive() }),
});
export const InviteNotFound = defineError({
  code: 'identity.invite_not_found', httpStatus: 404, message: 'errors.identity.invite_not_found', retryable: false,
  details: z.object({ inviteId: InviteId }),
});

const CLOUD_REASON = 'Users, memberships, roles and invitations are cloud-authoritative identity records; a node receives only what it needs to verify.';

export const ListRoles = defineQuery({
  name: 'identity.roles.list',
  summary: 'The roles a member of this location can hold: Super Admin first, then custom roles by name.',
  input: z.object({ locationId: LocationId }),
  output: z.object({ roles: z.array(RoleView) }),
  // Its readers are the Users and User Roles pages, Super Admin only (A53), like every write beside it. It answered any
  // back office session (coordinator 2026-10-18, review of #1387); legacy's API asked manage_staff_settings.
  permission: 'users.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/user-roles', 'GET /v1/user-permissions/definitions'],
});

export const ListMembers = defineQuery({
  name: 'identity.members.list',
  summary: "The location's backoffice users with their role and last sign-in, by name.",
  input: z.object({ locationId: LocationId, search: z.string().trim().max(100).optional() }),
  output: z.object({ members: z.array(MemberView) }),
  permission: 'users.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/location-users'],
});

/** The account's account-wide users (legacy `account_user`, the staff Account Detail's "Account Users"), by name. */
export const ListAccountMembers = defineQuery({
  name: 'identity.accountMembers.list',
  summary: "The account's account-wide backoffice users (they open every location), by name.",
  input: z.object({}),
  output: z.object({ members: z.array(MemberView) }),
  permission: 'users.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/accounts/{id}/users'],
});

/**
 * A user's access as the staff User Access dialog shows it (legacy `UserAccess.vue`, DLG-133): the account when they are
 * an account-wide user, and every location of this account they can open with the role they hold there. `direct` is a
 * location's own user (legacy `location_user_direct_access`), the only kind removed or re-roled from a location.
 */
export const UserAccessView = z
  .object({
    userId: UserId,
    name: z.string(),
    accounts: z.array(z.object({ accountId: AccountId, name: z.string() })),
    locations: z.array(z.object({ locationId: LocationId, name: z.string(), slug: z.string(), roleName: z.string(), direct: z.boolean() })),
  })
  .meta({ id: 'UserAccessView' });

export const UserAccess = defineQuery({
  name: 'identity.userAccess',
  summary: "One user's access in this account: account-wide or not, and each location they open with its role (staff User Access).",
  input: z.object({ userId: UserId }),
  output: UserAccessView,
  permission: 'users.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'account',
  errors: [MemberNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/accounts/{id}/users (user.accounts, user.locations)'],
});

export const ListInvites = defineQuery({
  name: 'identity.invites.list',
  summary: "The location's pending invitations, newest first.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ invites: z.array(InviteView) }),
  permission: 'users.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/location-users/invites'],
});

export const UpsertRole = defineCommand({
  name: 'identity.upsertRole',
  summary: "Create a location's custom role or change its name, description or permissions.",
  input: z.object({
    roleId: RoleId,
    locationId: LocationId,
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(255).nullable(),
    permissions: z.array(PermissionKeyEnum).min(1).max(PERMISSION_KEYS.length),
  }),
  output: RoleView,
  permission: 'users.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [RoleNotFound, SystemRoleLocked],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/user-roles', 'PUT /v1/user-roles/{id}'],
});

export const DeleteRole = defineCommand({
  name: 'identity.deleteRole',
  summary: "Delete a custom role; its members move to Super Admin (legacy behaviour).",
  input: z.object({ roleId: RoleId, locationId: LocationId }),
  output: z.object({ roleId: RoleId, reassigned: z.int().nonnegative() }),
  permission: 'users.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [RoleNotFound, SystemRoleLocked],
  dryRun: true,
  agent: { expose: false },
  legacy: ['DELETE /v1/user-roles/{id}'],
});

export const InviteUser = defineCommand({
  name: 'identity.inviteUser',
  summary: 'Invite someone by email to sign in at this location with a role; they set a password or connect their account. Delivery `link` returns the accept link to the inviter instead of mailing it.',
  input: z.object({ inviteId: InviteId, locationId: LocationId, email: Email, roleId: RoleId, delivery: InviteDelivery.default('email') }),
  output: SentInvite,
  permission: 'users.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [RoleNotFound, AlreadyMember, AlreadyInvited, InviteMailLimited],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/locations/{id}/invite'],
});

export const ResendInvite = defineCommand({
  name: 'identity.resendInvite',
  summary: 'Send a pending invitation again with a fresh link valid for seven days (the old link stops working). Delivery `link` returns the new link to the inviter instead of mailing it.',
  input: z.object({ inviteId: InviteId, locationId: LocationId, delivery: InviteDelivery.default('email') }),
  output: SentInvite,
  permission: 'users.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [InviteNotFound, InviteMailLimited],
  dryRun: false,
  agent: { expose: false },
  legacy: ['GET /v1/invites/{id}/resend'],
});

export const CancelInvite = defineCommand({
  name: 'identity.cancelInvite',
  summary: 'Cancel a pending invitation; its link stops working.',
  input: z.object({ inviteId: InviteId, locationId: LocationId }),
  output: z.object({ inviteId: InviteId }),
  permission: 'users.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [InviteNotFound],
  dryRun: false,
  agent: { expose: false },
  legacy: ['DELETE /v1/invites/{id}'],
});

export const ChangeMemberRole = defineCommand({
  name: 'identity.changeMemberRole',
  summary: "Change the role a user holds at this location.",
  input: z.object({ userId: UserId, locationId: LocationId, roleId: RoleId }),
  output: MemberView,
  permission: 'users.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [RoleNotFound, MemberNotFound, OwnMembership, AccountMember, LastAdmin],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/users/{userId}/locations/{locationId}/role'],
});

export const RemoveMember = defineCommand({
  name: 'identity.removeMember',
  summary: 'Remove a user from this location; every token of theirs that names it stops working at once (B1800), a session left nowhere to go ends, and one that still holds somewhere moves there at its next refresh.',
  input: z.object({ userId: UserId, locationId: LocationId }),
  output: z.object({ userId: UserId }),
  permission: 'users.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [MemberNotFound, OwnMembership, AccountMember, LastAdmin],
  dryRun: true,
  agent: { expose: false },
  legacy: ['DELETE /v1/locations/{location_id}/users/{user_id}'],
});

/** DLG-135 No Location: legacy will not invite to an account that has no location yet. */
export const AccountHasNoLocation = defineError({
  code: 'identity.account_has_no_location', httpStatus: 422, message: 'errors.identity.account_has_no_location', retryable: false,
  details: z.object({}),
});

/**
 * DLG-134 Account User Invite (legacy `POST accounts/{id}/invite`, AccountController::invite): an invitation to the
 * whole account. Accepted, it makes the person an account-wide user (every location, as Super Admin: legacy's account
 * users hold no role). Sent from the staff Accounts list and Account Detail, so it names its account.
 */
export const InviteAccountUser = defineCommand({
  name: 'identity.inviteAccountUser',
  summary: 'Invite someone by email to be an account-wide user of the account (every location). Delivery `link` returns the accept link instead of mailing it.',
  input: z.object({ inviteId: InviteId, accountId: AccountId, email: Email, delivery: InviteDelivery.default('email') }),
  output: SentInvite,
  // Legacy offers it on staff pages only (Radish admins and resellers).
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [AlreadyMember, AlreadyInvited, AccountHasNoLocation, InviteMailLimited],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/accounts/{id}/invite'],
});

/** Remove Account User (legacy `DELETE accounts/{id}/users/{user}`, Radish admins and resellers): the account-wide membership ends. */
export const RemoveAccountUser = defineCommand({
  name: 'identity.removeAccountUser',
  summary: "Remove an account-wide user from the account; their location memberships stay. Every token of theirs for the account stops working at once (B1800), a session left nowhere to go ends, and one that still holds somewhere moves there at its next refresh.",
  input: z.object({ accountId: AccountId, userId: UserId }),
  output: z.object({ userId: UserId }),
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [MemberNotFound, OwnMembership],
  dryRun: true,
  agent: { expose: false },
  legacy: ['DELETE /v1/accounts/{account_id}/users/{user_id}'],
});

// ---- DLG-136 Edit User (legacy `PUT users/{id}`, Radish admins and resellers; field note backoffice.accounts.detail) ---

/** A back-office user as Radish staff edit them: Basic Info (name, email, phone, time zone). */
export const AccountUserView = z
  .object({ userId: UserId, name: z.string(), email: z.string(), phone: z.string().nullable(), timeZone: z.string().nullable() })
  .meta({ id: 'AccountUserView' });

export const EmailTaken = defineError({
  code: 'identity.email_taken', httpStatus: 422, message: 'errors.identity.email_taken', retryable: false,
  details: z.object({}),
});

export const PasswordInvalid = defineError({
  code: 'identity.password_invalid', httpStatus: 422, message: 'errors.identity.password_invalid', retryable: false,
  details: z.object({ problem: z.enum(['too_short', 'too_long']) }),
});

export const UpdateUser = defineCommand({
  name: 'identity.updateUser',
  summary: "Radish staff or the account's reseller change a user of the account: name, email, phone, time zone, and a new password (left out keeps it).",
  input: z.object({
    accountId: AccountId,
    userId: UserId,
    name: z.string().trim().min(1).max(255),
    email: Email,
    phone: z.string().trim().regex(/^[0-9]{10,15}$/).nullable().optional(),
    timeZone: LocationZone.nullable().optional(),
    password: secret(z.string().min(1).max(256)).optional(),
  }),
  output: AccountUserView,
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: CLOUD_REASON,
  emits: [],
  errors: [MemberNotFound, EmailTaken, PasswordInvalid],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/users/{id}'],
});

export const AccountUser = defineQuery({
  name: 'identity.accountUser',
  summary: 'One user of the account as the staff Edit User dialog shows them (never their password).',
  input: z.object({ userId: UserId }),
  output: AccountUserView,
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: CLOUD_REASON,
  scope: 'account',
  errors: [MemberNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/users/{id}'],
});

// ---- SCR-011/012 the staff Users page (legacy `/users/list`, UsersList.vue; field note backoffice.admin.users.list) ------

const PLATFORM_USERS_CLOUD = 'Every back-office user across every account is a platform-wide read only the cloud holds.';

/**
 * One back-office user as the staff Users page lists them (legacy UserResource): `accounts` counts the accounts they
 * reach (each account-wide membership, or every account of their reseller), `locations` the distinct open locations
 * (every location of those accounts plus their own location memberships), as legacy's approvedAccounts/Locations.
 */
export const PlatformUserView = z
  .object({ userId: UserId, name: z.string(), email: z.string(), phone: z.string().nullable(), accounts: z.int().nonnegative(), locations: z.int().nonnegative() })
  .meta({ id: 'PlatformUserView' });

export const ListPlatformUsers = defineQuery({
  name: 'identity.platformUsers.list',
  summary: 'Every back-office user on the platform but the caller, by name, with how many accounts and locations each reaches (staff Users page).',
  input: z.object({}),
  output: z.object({ users: z.array(PlatformUserView) }),
  // Radish admins only, as legacy's page (REQUIRES_ADMIN). Legacy's API also answered resellers with every user on the
  // platform (B342); that is not carried over (RADISH_STAFF_ONLY_PERMISSIONS, L498).
  permission: 'users.platform.view',
  availability: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/users'],
});

/**
 * DLG-133 from the staff Users page: one user's access across the platform. Each account they reach account-wide, then
 * every open location they reach with its account and the role they hold there (`direct` = a location's own user).
 */
export const PlatformUserAccessView = z
  .object({
    userId: UserId,
    name: z.string(),
    accounts: z.array(z.object({ accountId: AccountId, name: z.string() })),
    locations: z.array(z.object({ accountId: AccountId, locationId: LocationId, name: z.string(), slug: z.string(), roleName: z.string(), direct: z.boolean() })),
  })
  .meta({ id: 'PlatformUserAccessView' });

export const PlatformUserAccess = defineQuery({
  name: 'identity.platformUserAccess',
  summary: "One user's accounts and locations across the platform, with the role at each location (User Access on the staff Users page).",
  input: z.object({ userId: UserId }),
  output: PlatformUserAccessView,
  permission: 'users.platform.view',
  availability: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  scope: 'platform',
  errors: [MemberNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/users/{id}'],
});

// ---- SCR-012 Create User and Edit User on the staff Users page (legacy UsersItem.vue; `POST users`, `PUT users/{id}`) -----

/** A create whose minted user id another command already used (a retry of the same command replays instead). */
export const UserExists = defineError({
  code: 'identity.user_exists', httpStatus: 409, message: 'errors.identity.user_exists', retryable: false,
  details: z.object({ userId: UserId }),
});

const PlatformUserFields = {
  name: z.string().trim().min(1).max(255),
  email: Email,
  phone: z.string().trim().regex(/^[0-9]{10,15}$/).nullable().optional(),
  timeZone: LocationZone.nullable().optional(),
};

export const CreatePlatformUser = defineCommand({
  name: 'identity.createPlatformUser',
  summary: 'A Radish admin adds a back-office user from the staff Users page: name, email, phone, time zone and password. The new user belongs to no account.',
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried create replays. */
    userId: UserId,
    ...PlatformUserFields,
    password: secret(z.string().min(1).max(256)),
  }),
  output: AccountUserView,
  // Radish admins only, as legacy `POST users` (UserController@store authorizes ADMIN alone).
  permission: 'users.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  emits: [],
  errors: [UserExists, EmailTaken, PasswordInvalid],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/users'],
});

export const UpdatePlatformUser = defineCommand({
  name: 'identity.updatePlatformUser',
  summary: "A Radish admin changes any back-office user from the staff Users page: name, email, phone, time zone, and a new password (left out keeps it).",
  input: z.object({ userId: UserId, ...PlatformUserFields, password: secret(z.string().min(1).max(256)).optional() }),
  output: AccountUserView,
  // Radish admins only, the staff Users page's gate. Legacy `PUT users/{id}` also let any reseller edit any user by id
  // (B213); a reseller edits its own accounts' users through `identity.updateUser`.
  permission: 'users.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  emits: [],
  errors: [MemberNotFound, EmailTaken, PasswordInvalid],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/users/{id}'],
});

export const PlatformUser = defineQuery({
  name: 'identity.platformUser',
  summary: 'One back-office user as the staff Edit User dialog shows them, whatever accounts they belong to (never their password).',
  input: z.object({ userId: UserId }),
  output: AccountUserView,
  permission: 'users.platform.manage',
  availability: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  scope: 'platform',
  errors: [MemberNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/users/{id}'],
});

/** A Radish staff role, or null for none (identity.radish_staff; doc 18: support and admin may use the Radish Broker). */
export const RadishStaffRole = z.enum(['admin', 'support', 'viewer']).meta({ id: 'RadishStaffRole' });

/**
 * A Radish admin makes a back-office user Radish staff, changes their staff role, or ends it (role null). Ending it
 * stops every Radish Broker key the person holds at once (ops plan 2026-10-14-staff-agents). An admin can't change their
 * own staff role, so the platform never loses its last admin by accident.
 */
export const SetRadishStaffRole = defineCommand({
  name: 'identity.setRadishStaffRole',
  summary: "A Radish admin grants, changes or ends a back-office user's Radish staff role (admin, support or viewer; null ends it).",
  input: z.object({ userId: UserId, role: RadishStaffRole.nullable() }),
  output: z.object({ userId: UserId, role: RadishStaffRole.nullable() }),
  permission: 'users.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: PLATFORM_USERS_CLOUD,
  emits: [],
  errors: [MemberNotFound, OwnMembership],
  dryRun: true,
  agent: { expose: false },
});

export const identityQueries = [ListRoles, ListMembers, ListInvites, ListAccountMembers, UserAccess, AccountUser, ListPlatformUsers, PlatformUserAccess, PlatformUser] as const;
export const identityCommands = [UpsertRole, DeleteRole, InviteUser, ResendInvite, CancelInvite, ChangeMemberRole, RemoveMember, InviteAccountUser, RemoveAccountUser, UpdateUser, CreatePlatformUser, UpdatePlatformUser, SetRadishStaffRole] as const;
